import { logEvent } from "@src/appV2/lib/analytics";
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";

import { STEP_UP_EVENTS } from "./constants";
import { StepUpModal } from "./StepUpModal";
import { registerStepUpHandler, type StepUpRequest } from "./stepUpController";

/**
 * Hosts the PHI step-up modal and registers the handler the axios interceptor calls when a
 * home-health-api response is 403 STEP_UP_REQUIRED. Mount once, high in the app tree.
 */
export function StepUpProvider({ children }: { children: ReactNode }): JSX.Element {
  const [request, setRequest] = useState<StepUpRequest | undefined>();
  const resolverRef = useRef<((reAuthenticated: boolean) => void) | undefined>(undefined);

  useEffect(() => {
    registerStepUpHandler(
      async (nextRequest) =>
        new Promise<boolean>((resolve) => {
          resolverRef.current = resolve;
          setRequest(nextRequest);
          logEvent(STEP_UP_EVENTS.REQUIRED_SHOWN, { stepUpMethod: nextRequest.stepUpMethod });
        })
    );
    return () => registerStepUpHandler(undefined);
  }, []);

  const settle = useCallback((reAuthenticated: boolean) => {
    resolverRef.current?.(reAuthenticated);
    resolverRef.current = undefined;
    setRequest(undefined);
  }, []);

  return (
    <>
      {children}
      {request ? (
        <StepUpModal
          open
          stepUpMethod={request.stepUpMethod}
          onComplete={() => {
            logEvent(STEP_UP_EVENTS.COMPLETED, { stepUpMethod: request.stepUpMethod });
            settle(true);
          }}
          onCancel={() => {
            logEvent(STEP_UP_EVENTS.CANCELLED, { stepUpMethod: request.stepUpMethod });
            settle(false);
          }}
        />
      ) : null}
    </>
  );
}
